import styled from '@emotion/styled';
import { Button } from 'antd';
import { type FC, type MutableRefObject, type ReactNode, useEffect, useRef } from 'react';

const FollowButtonRoot = styled(Button)`
  position: relative;
  overflow: hidden;

  /* keep the icon/spinner above the progress fill */
  .ant-btn-icon {
    position: relative;
    z-index: 1;
  }
`;

// Translucent fill whose width is driven imperatively by FollowButton's rAF loop.
const FollowFill = styled.span`
  position: absolute;
  inset: 0 auto 0 0;
  width: 0;
  background: rgba(255, 255, 255, 0.35);
  pointer-events: none;
  z-index: 0;
`;

// Keeps the label above the fill.
const FollowLabel = styled.span`
  position: relative;
  z-index: 1;
`;

/**
 * Follow button with a fill bar that counts down to the next poll. It runs its own
 * requestAnimationFrame loop and writes the fill width directly to the DOM, so neither
 * the parent component nor this button re-renders per frame. The countdown is anchored
 * to `cycleStartRef`, which the parent stamps each time a poll tick completes — this
 * keeps the bar in sync with the real (variable-length) poll cadence instead of drifting.
 */
export const FollowButton: FC<{
  active: boolean;
  durationMs: number;
  cycleStartRef: MutableRefObject<number>;
  loading?: boolean;
  icon?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  children?: ReactNode;
}> = ({ active, durationMs, cycleStartRef, loading, icon, disabled, onClick, children }) => {
  const fillRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!active) {
      if (fillRef.current != null) fillRef.current.style.width = '0%';
      return;
    }
    // Start a fresh countdown the moment Follow turns on; the parent re-stamps it per tick.
    cycleStartRef.current = Date.now();
    let frame = 0;
    const tick = () => {
      const pct = Math.min(100, ((Date.now() - cycleStartRef.current) / durationMs) * 100);
      if (fillRef.current != null) fillRef.current.style.width = `${pct}%`;
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, durationMs, cycleStartRef]);

  return (
    <FollowButtonRoot
      type="primary"
      loading={loading}
      icon={icon}
      disabled={disabled}
      onClick={onClick}
    >
      <FollowFill ref={fillRef} aria-hidden />
      <FollowLabel>{children}</FollowLabel>
    </FollowButtonRoot>
  );
};
